import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {LoadingButton} from '@mui/lab';
import {Box, Divider, IconButton, Tooltip} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {ExportField} from '../../../interfaces/Export';
import {ShaftClearanceAssetListQuery} from '../../../interfaces/ShaftClearance';
import {saveFile} from '../../../utils/file';
import {CloseSnackbarButton} from '../../common/CloseSnackbarButton';
import ExportFieldForm from '../../common/ExportFieldForm';
import ExportPdfButton from '../../common/ExportPdfButton';
import ModalFixed from '../../common/ModalFixed';
import SnackbarMessages from '../../common/SnackbarMessages';

interface Props {
  params: ShaftClearanceAssetListQuery;
  exportFields?: ExportField[];
}

const ShaftClearanceAssetGridExportButton = ({
  params,
  exportFields,
}: Props) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [isOpened, setIsOpened] = useState(false);
  const [isExcelModalOpened, setIsExcelModalOpened] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const fetchData = async (fields: string[]) => {
    setIsExcelModalOpened(false);
    setIsFetching(true);

    try {
      const exportParams = {
        export: 'excel',
        fields,
        date: params.date,
        order: params.order,
        dir: params.dir,
        location: params.location,
        section_id: params.section_id,
      };

      const resp = await API.get(`${apiBaseUrl}/shaft-clearance/asset/grid`, {
        params: exportParams,
        responseType: 'blob',
      });
      saveFile(
        resp.data,
        `ShaftClearanceAsset-${params.date || new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (error: unknown) {
      const messages = getMessagesFromApiError(error, 'STRA_d491f798');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: (key) => (
          <CloseSnackbarButton onClick={() => closeSnackbar(key)} />
        ),
      });
    }
    setIsFetching(false);
  };

  return (
    <>
      <Tooltip title="Export">
        <FileDownloadIcon onClick={() => setIsOpened(true)} />
      </Tooltip>

      {isOpened ? (
        <ModalFixed open={isOpened} onClose={() => setIsOpened(false)}>
          <Box
            display="flex"
            flexDirection="column"
            position="relative"
            gap={3}
            p={3}
          >
            <Box display="flex" justifyContent="space-between">
              <Box display="flex" alignItems="center" width="100%" gap={1.5}>
                <FileDownloadIcon sx={{color: 'primary.main'}} />
                <Box fontSize={24}>Export Shaft Clearance Assets</Box>
              </Box>
              <IconButton onClick={() => setIsOpened(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              position="relative"
              gap={3}
            >
              <Divider>Export</Divider>
              <LoadingButton
                fullWidth
                size="large"
                variant="contained"
                loading={isFetching}
                onClick={() => setIsExcelModalOpened(true)}
              >
                Export to Excel
              </LoadingButton>
              <ExportPdfButton
                endpoint="/shaft-clearance/asset/grid"
                defaultFilename="shaft_clearance_assets.pdf"
                params={params as unknown as Record<string, unknown> | undefined}
                exportFields={exportFields}
              />
            </Box>
          </Box>
        </ModalFixed>
      ) : null}

      <ModalFixed
        open={isExcelModalOpened}
        onClose={() => setIsExcelModalOpened(false)}
      >
        <Box
          display="flex"
          flexDirection="column"
          position="relative"
          gap={3}
          p={3}
        >
          <ExportFieldForm
            exportFields={exportFields}
            onChange={fetchData}
            close={() => setIsExcelModalOpened(false)}
            loading={isFetching}
            columns={3}
          />
        </Box>
      </ModalFixed>
    </>
  );
};

export default ShaftClearanceAssetGridExportButton;
